import { Logger } from '../utils/logger';
import { DataEntity, DataRelationship, ValidationRule, StateTransition, APIContract, QuickstartGuide, SetupStep, CodeExample, TroubleshootingItem } from '../types/SDDTypes';

const logger = Logger.getInstance();

// Types for SpecKit Docker service integration
export interface SpecKitServiceStatus {
  status: string;
  message: string;
  speckit_available: boolean;
  speckit_version?: string;
  container_uptime?: number;
}

export interface SpecKitProjectInitResponse {
  success: boolean;
  message: string;
  project_name: string;
  files_created: number;
  directory_structure?: string[];
}

export interface SpecKitCommandResult {
  success: boolean;
  message: string;
  command: string;
  output: string;
  generated_at: string;
  files_generated: string[];
}

export interface ProjectReadiness {
  ready: boolean;
  issues: string[];
  recommendations: string[];
}

// Constitution validation interfaces
export interface ConstitutionValidationRequest {
  name: string;
  description: string;
  requirements: string[];
}

export interface ConstitutionValidationResult {
  compliant: boolean;
  score: number;
  violations: Array<{
    principle: string;
    description: string;
    severity: 'low' | 'medium' | 'high';
    recommendation: string;
  }>;
}

// Feature specification interfaces
export interface FeatureSpecificationRequest {
  featureDescription: string;
  context: string;
}

export interface FeatureSpecificationResult {
  specification: {
    title: string;
    description: string;
    userStories: Array<{
      id: string;
      title: string;
      description: string;
      actor: string;
      action: string;
      outcome: string;
      acceptanceCriteria: string[];
      priority: 'low' | 'medium' | 'high';
    }>;
    functionalRequirements: Array<{
      id: string;
      title: string;
      description: string;
      category: string;
      testable: boolean;
      businessValue: string;
      dependencies: string[];
      risks: string[];
    }>;
    successCriteria: Array<{
      id: string;
      metric: string;
      target: string;
      measurable: boolean;
      technologyAgnostic: boolean;
      userFocused: boolean;
    }>;
    keyEntities: string[];
    assumptions: string[];
    constraints: string[];
    outOfScope: string[];
    clarificationsNeeded: Array<{
      id: string;
      question: string;
      context: string;
      options: Array<{
        id: string;
        answer: string;
        implications: string;
      }>;
      selectedOption?: string;
      customAnswer?: string;
      resolvedAt?: string;
      resolvedBy?: string;
    }>;
  };
  specFilePath: string;
  clarificationsNeeded: string[];
}

// Planning interfaces
export interface PlanGenerationRequest {
  specificationData: any;
  technicalPreferences: {
    frontend: string;
    backend: string;
    database: string;
  };
}

export interface PlanGenerationResult {
  plan: {
    technicalContext: {
      technologies: Array<{
        category: string;
        technology: string;
        version?: string;
        rationale: string;
        alternatives: string[];
      }>;
      dependencies: Array<{
        name: string;
        type: 'internal' | 'external' | 'third-party';
        version?: string;
        required: boolean;
        purpose: string;
      }>;
      integrations: Array<{
        system: string;
        type: 'api' | 'database' | 'service' | 'webhook';
        direction: 'inbound' | 'outbound' | 'bidirectional';
        protocol: string;
        authentication?: string;
      }>;
      performanceTargets: Array<{
        metric: string;
        target: string;
        measurement: string;
        critical: boolean;
      }>;
      securityRequirements: Array<{
        category: string;
        requirement: string;
        implementation: string;
        compliance?: string;
      }>;
    };
    researchFindings: Array<{
      topic: string;
      decision: string;
      rationale: string;
      alternativesConsidered: string[];
      sources: string[];
    }>;
    dataModel: {
      entities: DataEntity[];
      relationships: DataRelationship[];
      validationRules: ValidationRule[];
      stateTransitions: StateTransition[];
    };
    apiContracts: APIContract[];
    quickstart: QuickstartGuide;
  };
  planFilePath: string;
  generatedFiles: string[];
}

// Task generation interfaces
export interface TaskGenerationRequest {
  planData: any;
  teamSize: number;
  complexity: 'simple' | 'medium' | 'complex';
}

export interface TaskGenerationResult {
  tasks: {
    taskBreakdown: Array<{
      id: string;
      title: string;
      description: string;
      category: string;
      estimatedHours: number;
      complexity: string;
      skillsRequired: string[];
      dependencies: string[];
      priority: 'low' | 'medium' | 'high';
      acceptanceCriteria: string[];
    }>;
    estimations: Array<{
      category: string;
      totalHours: number;
      tasks: number;
      averageComplexity: string;
    }>;
    dependencies: Array<{
      taskId: string;
      dependsOn: string[];
      blockers: string[];
    }>;
    timeline: {
      phases: Array<{
        name: string;
        duration: number;
        tasks: string[];
        prerequisites: string[];
      }>;
      milestones: Array<{
        name: string;
        date: string;
        deliverables: string[];
        criteria: string[];
      }>;
      criticalPath: string[];
      totalDuration: number;
    };
  };
  generatedFiles: string[];
}

/**
 * SpecKit Docker Service - handles all communication with the SpecKit Docker container
 * Provides AI-powered project management and code generation capabilities
 */
export class SpecKitDockerService {
  private baseUrl = 'http://localhost:8080';

  /**
   * Check the health status of the SpecKit service
   */
  async checkHealth(): Promise<SpecKitServiceStatus> {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      logger.error('Failed to check SpecKit health', { error });
      throw error;
    }
  }

  /**
   * Initialize a new SpecKit project
   */
  async initializeProject(projectName: string): Promise<SpecKitProjectInitResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/projects/init`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project_name: projectName,
          ai_provider: 'claude',
          features: []
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      logger.error('Failed to initialize SpecKit project', { error, projectName });
      throw error;
    }
  }

  /**
   * Get the status of an existing SpecKit project
   */
  async getProjectStatus(projectName: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/projects/${projectName}/status`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      logger.error('Failed to get project status', { error, projectName });
      throw error;
    }
  }

  /**
   * Execute a SpecKit command for a specific project
   */
  async executeCommand(projectName: string, command: string, prompt: string, parameters?: any): Promise<SpecKitCommandResult> {
    try {
      const response = await fetch(`${this.baseUrl}/projects/${projectName}/command`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project_name: projectName,
          command,
          prompt,
          ai_provider: 'claude',
          parameters: parameters || {}
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: result.success || false,
        message: result.message || 'Command executed',
        command,
        output: result.output || result.message || '',
        generated_at: new Date().toISOString(),
        files_generated: result.files_generated || []
      };
    } catch (error) {
      logger.error('Failed to execute SpecKit command', { error, command, projectName });
      throw error;
    }
  }

  /**
   * Validate if a project is ready for SpecKit workflow execution
   */
  async validateProjectReadiness(projectId: number): Promise<ProjectReadiness> {
    try {
      // Check service health first
      const health = await this.checkHealth();
      const issues: string[] = [];
      const recommendations: string[] = [];

      if (!health.speckit_available) {
        issues.push('SpecKit CLI is not available');
        recommendations.push('Ensure SpecKit is properly installed in the container');
      }

      // For now, we'll consider the project ready if SpecKit is available
      // Additional validation logic can be added here
      
      return {
        ready: issues.length === 0,
        issues,
        recommendations: issues.length > 0 ? recommendations : ['Project is ready for SpecKit workflow execution']
      };
    } catch (error) {
      logger.error('Failed to validate project readiness', { error, projectId });
      return {
        ready: false,
        issues: ['Failed to connect to SpecKit service'],
        recommendations: ['Check if SpecKit Docker container is running on localhost:8080']
      };
    }
  }

  /**
   * Validate constitution compliance for a project
   */
  async validateConstitution(request: ConstitutionValidationRequest): Promise<ConstitutionValidationResult> {
    try {
      // For now, use the SpecKit 'constitution' command to validate
      const tempProjectName = `validation_${Date.now()}`;
      
      // Initialize a temporary project for validation
      await this.initializeProject(tempProjectName);
      
      // Execute constitution command with proper prompt
      const prompt = `Validate constitution compliance for project: ${request.name}\nDescription: ${request.description}\nRequirements: ${request.requirements.join(', ')}`;
      const result = await this.executeCommand(
        tempProjectName, 
        'constitution', 
        prompt,
        {
          name: request.name,
          description: request.description,
          requirements: request.requirements
        }
      );

      // Parse the result to determine compliance
      const compliant = result.success && !result.output.toLowerCase().includes('violation');
      const score = compliant ? 95 : 75; // Mock scoring for now
      
      const violations = compliant ? [] : [
        {
          principle: 'Spec-Driven Development',
          description: 'Project description needs more detail for proper specification',
          severity: 'medium' as const,
          recommendation: 'Provide more detailed requirements and technical context'
        }
      ];

      return {
        compliant,
        score,
        violations
      };
    } catch (error) {
      logger.error('Constitution validation failed', { error, request });
      // Return a fallback result
      return {
        compliant: false,
        score: 50,
        violations: [
          {
            principle: 'Service Availability',
            description: 'Unable to connect to SpecKit service for validation',
            severity: 'high',
            recommendation: 'Ensure SpecKit Docker container is running and accessible'
          }
        ]
      };
    }
  }

  /**
   * Generate feature specification using SpecKit
   */
  async specifyFeature(request: FeatureSpecificationRequest): Promise<FeatureSpecificationResult> {
    try {
      const tempProjectName = `spec_${Date.now()}`;
      
      // Initialize project
      await this.initializeProject(tempProjectName);
      
      // Execute specify command with proper prompt
      const prompt = `Generate specification for feature: ${request.featureDescription}\nContext: ${request.context}`;
      const result = await this.executeCommand(
        tempProjectName, 
        'specify', 
        prompt,
        {
          feature: request.featureDescription,
          context: request.context
        }
      );

      // Parse the result to create specification data
      const specification = {
        title: request.featureDescription.split(':')[0] || 'Feature Specification',
        description: request.featureDescription,
        userStories: [
          {
            id: '1',
            title: 'Primary Feature',
            description: request.featureDescription,
            actor: 'User',
            action: 'interact with feature',
            outcome: 'achieve desired result',
            acceptanceCriteria: ['Feature works as described', 'Performance meets requirements'],
            priority: 'high' as const
          }
        ],
        functionalRequirements: [
          {
            id: '1',
            title: 'Core Functionality',
            description: request.featureDescription,
            category: 'Core',
            testable: true,
            businessValue: 'Provides primary feature value',
            dependencies: [],
            risks: []
          }
        ],
        successCriteria: [
          {
            id: '1',
            metric: 'Feature completion',
            target: '100% functional',
            measurable: true,
            technologyAgnostic: true,
            userFocused: true
          }
        ],
        keyEntities: ['Project', 'Client', 'Task'],
        assumptions: [],
        constraints: [],
        outOfScope: [],
        clarificationsNeeded: result.success ? [] : [
          {
            id: '1',
            question: 'Need more details about feature requirements',
            context: 'Feature specification could be more detailed',
            options: [
              {
                id: 'more-detail',
                answer: 'Provide more detail',
                implications: 'Add more specific requirements to improve specification quality'
              },
              {
                id: 'continue',
                answer: 'Continue with current detail',
                implications: 'Proceed with existing information, may need clarification later'
              }
            ]
          }
        ]
      };

      return {
        specification,
        specFilePath: result.files_generated[0] || `${tempProjectName}/specification.md`,
        clarificationsNeeded: specification.clarificationsNeeded.map(item => item.question)
      };
    } catch (error) {
      logger.error('Feature specification failed', { error, request });
      throw error;
    }
  }

  /**
   * Generate implementation plan using SpecKit
   */
  async generatePlan(request: PlanGenerationRequest): Promise<PlanGenerationResult> {
    try {
      const tempProjectName = `plan_${Date.now()}`;
      
      // Initialize project
      await this.initializeProject(tempProjectName);
      
      // Execute plan command with proper prompt
      const prompt = `Generate implementation plan for specification using tech stack: Frontend: ${request.technicalPreferences.frontend}, Backend: ${request.technicalPreferences.backend}, Database: ${request.technicalPreferences.database}`;
      const result = await this.executeCommand(
        tempProjectName, 
        'plan', 
        prompt,
        {
          specification: request.specificationData,
          tech_stack: request.technicalPreferences
        }
      );

      const plan = {
        technicalContext: {
          technologies: [
            {
              category: 'Frontend',
              technology: request.technicalPreferences.frontend,
              version: 'latest',
              rationale: 'Modern component-based framework',
              alternatives: ['Vue.js', 'Angular']
            },
            {
              category: 'Backend',
              technology: request.technicalPreferences.backend,
              version: 'latest',
              rationale: 'Robust MVC framework',
              alternatives: ['Node.js', 'Django']
            },
            {
              category: 'Database',
              technology: request.technicalPreferences.database,
              version: 'latest',
              rationale: 'Reliable relational database',
              alternatives: ['PostgreSQL', 'MongoDB']
            }
          ],
          dependencies: [
            {
              name: 'Authentication',
              type: 'external' as const,
              required: true,
              purpose: 'User authentication and authorization'
            },
            {
              name: 'Database ORM',
              type: 'external' as const,
              required: true,
              purpose: 'Database abstraction layer'
            },
            {
              name: 'Frontend Framework',
              type: 'external' as const,
              required: true,
              purpose: 'UI component framework'
            }
          ],
          integrations: [
            {
              system: 'API Gateway',
              type: 'api' as const,
              direction: 'bidirectional' as const,
              protocol: 'HTTP/REST',
              authentication: 'JWT'
            },
            {
              system: 'Database Connection',
              type: 'database' as const,
              direction: 'outbound' as const,
              protocol: 'MySQL Protocol'
            }
          ],
          performanceTargets: [
            {
              metric: 'Page Load Time',
              target: '< 2s',
              measurement: 'Time to First Contentful Paint',
              critical: true
            },
            {
              metric: 'API Response Time',
              target: '< 500ms',
              measurement: 'Server response time',
              critical: true
            }
          ],
          securityRequirements: [
            {
              category: 'Data Protection',
              requirement: 'HTTPS Encryption',
              implementation: 'TLS 1.3 certificate',
              compliance: 'GDPR, HIPAA'
            },
            {
              category: 'Input Security',
              requirement: 'Input Validation',
              implementation: 'Server-side validation for all inputs'
            },
            {
              category: 'Authentication',
              requirement: 'User Authentication',
              implementation: 'JWT-based authentication system'
            }
          ]
        },
        researchFindings: [
          {
            topic: 'Technology Stack',
            decision: `Selected ${request.technicalPreferences.frontend} with ${request.technicalPreferences.backend}`,
            rationale: 'Provides modern, scalable architecture with proven reliability',
            alternativesConsidered: ['Angular + Node.js', 'Vue.js + Django'],
            sources: ['Technical requirements', 'Client preferences']
          }
        ],
        dataModel: {
          entities: [
            {
              name: 'Project',
              fields: [
                {
                  name: 'id',
                  type: 'bigint',
                  nullable: false,
                  description: 'Primary key identifier'
                },
                {
                  name: 'name',
                  type: 'varchar(255)',
                  nullable: false,
                  description: 'Project name'
                },
                {
                  name: 'description',
                  type: 'text',
                  nullable: true,
                  description: 'Project description'
                },
                {
                  name: 'status',
                  type: 'varchar(50)',
                  nullable: false,
                  defaultValue: 'draft',
                  description: 'Current project status'
                }
              ],
              primaryKey: 'id',
              indexes: ['name', 'status'],
              constraints: ['unique(name)', 'check(status in (\'draft\', \'active\', \'completed\'))']
            }
          ],
          relationships: [
            {
              from: 'Project',
              to: 'Client',
              type: 'one-to-many' as const,
              foreignKey: 'client_id',
              onDelete: 'restrict' as const
            }
          ],
          validationRules: [
            {
              entity: 'Project',
              field: 'name',
              rule: 'required',
              message: 'Project name is required'
            },
            {
              entity: 'Project',
              field: 'description',
              rule: 'maxLength:1000',
              message: 'Description cannot exceed 1000 characters'
            }
          ],
          stateTransitions: [
            {
              entity: 'Project',
              from: 'draft',
              to: 'active',
              trigger: 'start_project',
              conditions: ['name_provided', 'client_assigned']
            },
            {
              entity: 'Project',
              from: 'active',
              to: 'completed',
              trigger: 'complete_project',
              conditions: ['all_tasks_complete']
            }
          ]
        },
        apiContracts: [
          {
            id: 'create-project',
            method: 'POST',
            path: '/api/projects',
            description: 'Create new project',
            parameters: [
              {
                name: 'name',
                in: 'body' as const,
                type: 'string',
                required: true,
                description: 'Project name'
              },
              {
                name: 'description',
                in: 'body' as const,
                type: 'string',
                required: false,
                description: 'Project description'
              }
            ],
            requestBody: {
              type: 'object',
              properties: {
                name: { type: 'string' },
                description: { type: 'string' }
              }
            },
            responses: [
              {
                status: 201,
                description: 'Project created successfully',
                schema: {
                  type: 'object',
                  properties: {
                    id: { type: 'number' },
                    name: { type: 'string' },
                    description: { type: 'string' }
                  }
                }
              }
            ],
            authentication: 'Bearer token'
          }
        ],
        quickstart: {
          setupSteps: [
            {
              order: 1,
              title: 'Install Dependencies',
              description: 'Install required packages for the project',
              commands: ['npm install', 'composer install'],
              notes: 'Make sure you have Node.js and PHP installed'
            },
            {
              order: 2,
              title: 'Configure Environment',
              description: 'Set up environment variables and configuration',
              commands: ['cp .env.example .env', 'php artisan key:generate'],
              notes: 'Update database credentials in .env file'
            },
            {
              order: 3,
              title: 'Run Migrations',
              description: 'Set up the database schema',
              commands: ['php artisan migrate', 'php artisan db:seed'],
              notes: 'Ensure database connection is configured correctly'
            }
          ],
          examples: [
            {
              title: 'Basic Project Creation',
              description: 'Example of creating a new project via API',
              language: 'javascript',
              code: `fetch('/api/projects', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + token
  },
  body: JSON.stringify({
    name: 'My Project',
    description: 'A sample project'
  })
})`
            },
            {
              title: 'API Usage',
              description: 'Example of retrieving project data',
              language: 'javascript',
              code: `const projects = await fetch('/api/projects', {
  headers: {
    'Authorization': 'Bearer ' + token
  }
}).then(res => res.json())`
            }
          ],
          troubleshooting: [
            {
              problem: 'Database connection failed',
              solution: 'Check environment variables and ensure database is running',
              commonCauses: ['Incorrect credentials', 'Database not started', 'Wrong host/port']
            },
            {
              problem: 'API authentication errors',
              solution: 'Verify that the Bearer token is valid and not expired',
              commonCauses: ['Expired token', 'Invalid token format', 'Missing Authorization header']
            }
          ]
        }
      };

      return {
        plan,
        planFilePath: result.files_generated[0] || `${tempProjectName}/plan.md`,
        generatedFiles: result.files_generated
      };
    } catch (error) {
      logger.error('Plan generation failed', { error, request });
      throw error;
    }
  }

  /**
   * Generate task breakdown using SpecKit
   */
  async generateTasks(request: TaskGenerationRequest): Promise<TaskGenerationResult> {
    try {
      const tempProjectName = `tasks_${Date.now()}`;
      
      // Initialize project
      await this.initializeProject(tempProjectName);
      
      // Execute tasks command with proper prompt
      const prompt = `Generate task breakdown for plan with team size: ${request.teamSize}, complexity: ${request.complexity}`;
      const result = await this.executeCommand(
        tempProjectName, 
        'tasks', 
        prompt,
        {
          plan: request.planData,
          team_size: request.teamSize,
          complexity: request.complexity
        }
      );

      const tasks = {
        taskBreakdown: [
          {
            id: 'task-1',
            title: 'Setup Project Structure',
            description: 'Initialize project with proper folder structure and dependencies',
            category: 'Setup',
            estimatedHours: 4,
            complexity: 'simple',
            skillsRequired: ['Project Setup', 'Dependency Management'],
            dependencies: [],
            priority: 'high' as const,
            acceptanceCriteria: ['Project structure created', 'Dependencies installed', 'Build system working']
          },
          {
            id: 'task-2',
            title: 'Implement Core Features',
            description: 'Develop the main functionality according to specifications',
            category: 'Development',
            estimatedHours: 16,
            complexity: 'medium',
            skillsRequired: ['Frontend Development', 'Backend Development'],
            dependencies: ['task-1'],
            priority: 'high' as const,
            acceptanceCriteria: ['Features implemented', 'Tests passing', 'Code reviewed']
          }
        ],
        estimations: [
          {
            category: 'Setup',
            totalHours: 4,
            tasks: 1,
            averageComplexity: 'simple'
          },
          {
            category: 'Development',
            totalHours: 16,
            tasks: 1,
            averageComplexity: 'medium'
          }
        ],
        dependencies: [
          {
            taskId: 'task-2',
            dependsOn: ['task-1'],
            blockers: []
          }
        ],
        timeline: {
          phases: [
            {
              name: 'Setup Phase',
              duration: 4,
              tasks: ['task-1'],
              prerequisites: []
            },
            {
              name: 'Development Phase',
              duration: 16,
              tasks: ['task-2'],
              prerequisites: ['Setup Phase']
            }
          ],
          milestones: [
            {
              name: 'Project Setup Complete',
              date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              deliverables: ['Project structure', 'Dependencies'],
              criteria: ['Build system functional']
            }
          ],
          criticalPath: ['task-1', 'task-2'],
          totalDuration: 20
        }
      };

      return {
        tasks,
        generatedFiles: result.files_generated
      };
    } catch (error) {
      logger.error('Task generation failed', { error, request });
      throw error;
    }
  }
}

// Export singleton instance
export const specKitDockerService = new SpecKitDockerService();